/*****************************************************
Naam: App.jsx
Locatie: /Dashboard/src/App.jsx
Aangeroepen door: index.js (React root)
Datum: 2026-01-08
Versie: 4.0.0
Auteur: Ragnar Standhardt
******************************************************
Beschrijving:
Hoofd App component met authenticatie, routing en quote form.

CHANGELOG v4.0.0: 🔐🎨 CUSTOMER PORTAL + BLUE THEME
- NEW: Customer portal routes (/customer/*)
- NEW: CustomerLogin route
- NEW: CustomerDashboard route
- NEW: React Router DOM voor routing
- IMPROVED: emerald → blue color scheme
- SECURITY: Separate customer/admin authentication

CHANGELOG v3.0.0: 💰 QUOTE SYSTEM ROUTING
- Feature: QuoteForm routing zonder authenticatie
- Route: /quote?token=...&vervoerder=...
- Fix: useEffect conditional call (React Hooks rules)
- Workflow: Vervoerders kunnen direct prijzen invullen

CHANGELOG v2.1.0:
- Toegevoegd: First-time setup voor nieuwe gebruikers
- Toegevoegd: Check voor bestaande dashboard credentials
- Flow: Setup → Login → Dashboard

CHANGELOG v2.0.0:
- Toegevoegd: Login systeem
- Toegevoegd: Session management
- Toegevoegd: Logout functionaliteit
- Beveiligd: Dashboard alleen toegankelijk na login

Functionaliteiten:
- First-time setup bij eerste gebruik
- Login scherm bij ongeauthenticeerde toegang
- Session check bij laden
- Dashboard render na succesvolle authenticatie
- Logout knop in dashboard
- QuoteForm voor vervoerders (geen auth)
- Customer portal voor klanten
- Blue color scheme (SendWell branding)
******************************************************/

import { LanguageProvider } from './LanguageContext';
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import FirstTimeSetup from './components/FirstTimeSetup';
import QuoteForm from './components/QuoteForm';
import CustomerLogin from './pages/CustomerLogin';
import CustomerDashboard from './pages/CustomerDashboard';
import CustomerTracking from './pages/CustomerTracking';
import CustomerNewRequest from './pages/CustomerNewRequest';
import CustomerMessages from './pages/CustomerMessages';
import CustomerHelpCenter from './pages/CustomerDashboard/CustomerHelpCenter';
import CustomerAnalytics from './pages/CustomerAnalytics';
import ConfirmPage from './pages/ConfirmPage';
import ErrorBoundary from './components/ErrorBoundary';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkInitialState();
  }, []);

  const checkInitialState = async () => {
    try {
      // Check of er al dashboard credentials zijn
      const setupResponse = await fetch('/api/check-setup');
      const setupResult = await setupResponse.json();
      
      if (!setupResult.setupComplete) {
        setNeedsSetup(true);
        setIsLoading(false);
        return;
      }

      // Check of er een actieve sessie is
      const sessionResponse = await fetch('/api/check-session');
      const sessionResult = await sessionResponse.json();
      
      if (sessionResult.authenticated) {
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Error checking initial state:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetupComplete = () => {
    setNeedsSetup(false);
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/logout', { method: 'POST' });
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-800 via-blue-700 to-blue-900">
        <div className="text-white text-xl">Laden...</div>
      </div>
    );
  }

  if (needsSetup) {
    return <FirstTimeSetup onSetupComplete={handleSetupComplete} />;
  }

  return (
    <ErrorBoundary>
      <LanguageProvider><Router>
        <Routes>
          {/* Quote Form (public - vervoerder prijsopgave) */}
          <Route path="/quote" element={<QuoteForm />} />
          
          {/* Confirm Page (public - klant bevestiging) */}
          <Route path="/confirm" element={<ConfirmPage />} />
          
          {/* Customer Portal Routes */}
          <Route path="/customer/login" element={<CustomerLogin />} />
          <Route path="/customer/dashboard" element={<CustomerDashboard />} />
          <Route path="/customer/tracking" element={<CustomerTracking />} />
          <Route path="/customer/new-request" element={<CustomerNewRequest />} />
          <Route path="/customer/messages" element={<CustomerMessages />} />
          <Route path="/customer/help" element={<CustomerHelpCenter />} />
          <Route path="/customer/analytics" element={<CustomerAnalytics aanvragen={[]} />} />
          
          {/* Admin Dashboard (protected) */}
          <Route 
            path="/" 
            element={
              isAuthenticated ? (
                <Dashboard onLogout={handleLogout} />
              ) : (
                <Login onLoginSuccess={handleLoginSuccess} />
              )
            } 
          />
          
          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router></LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
